import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const getBusinessUnitName = (item) => item?.business_unit || "";
const getLocationUnitName = (item) => item?.locations_units || "";

const normalizeBusinessUnitList = (payload) => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) return data;

  return [];
};

export const fetchActioncardsSets = createAsyncThunk(
  "actioncardsSet/fetchActioncardsSets",
  async (organisationId, { rejectWithValue, signal }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/organisation-business-unit/${organisationId}`,
        {
          headers: getHeaders(),
          signal,
        }
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch actioncards set")
      );
    }
  }
);

export const fetchActioncardsBusinessUnits = createAsyncThunk(
  "actioncardsSet/fetchActioncardsBusinessUnits",
  async (organisationId, { rejectWithValue, signal }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/all-business-units-names/${organisationId}`,
        {
          headers: getHeaders(),
          signal,
        }
      );

      return normalizeBusinessUnitList(res.data).map((item) => ({
        value: item.id,
        label: [`B${item.id}`, getBusinessUnitName(item)]
          .filter(Boolean)
          .join("-"),
        name: getBusinessUnitName(item),
      }));
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch business units")
      );
    }
  }
);

export const fetchActioncardsLocationsUnits = createAsyncThunk(
  "actioncardsSet/fetchActioncardsLocationsUnits",
  async (organisationId, { rejectWithValue, signal }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/all-locations-units-names/${organisationId}`,
        {
          headers: getHeaders(),
          signal,
        }
      );

      return normalizeBusinessUnitList(res.data).map((item) => ({
        value: item.id,
        label: [`L${item.id}`, getLocationUnitName(item)]
          .filter(Boolean)
          .join("-"),
        name: getLocationUnitName(item),
      }));
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch locations")
      );
    }
  }
);

export const updateActioncardsSetBusinessUnits = createAsyncThunk(
  "actioncardsSet/updateActioncardsSetBusinessUnits",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/organisation-business-unit`,
        data,
        {
          headers: getHeaders(),
        }
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Actioncards set update failed")
      );
    }
  }
);

const initialState = {
  items: [],
  businessUnitOptions: [],
  locationOptions: [],
  loading: false,
  optionsLoading: false,
  saving: false,
  error: null,
  successMessage: "",
};

const actioncardsSetSlice = createSlice({
  name: "actioncardsSet",
  initialState,
  reducers: {
    clearActioncardsSetToast: (state) => {
      state.error = null;
      state.successMessage = "";
    },
    resetActioncardsSetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActioncardsSets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActioncardsSets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
      })
      .addCase(fetchActioncardsSets.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.error = action.payload;
      })

      .addCase(fetchActioncardsBusinessUnits.pending, (state) => {
        state.optionsLoading = true;
        state.error = null;
      })
      .addCase(fetchActioncardsBusinessUnits.fulfilled, (state, action) => {
        state.optionsLoading = false;
        state.businessUnitOptions = action.payload || [];
      })
      .addCase(fetchActioncardsBusinessUnits.rejected, (state, action) => {
        state.optionsLoading = false;
        if (action.meta.aborted) return;
        state.error = action.payload;
      })

      .addCase(fetchActioncardsLocationsUnits.pending, (state) => {
        state.optionsLoading = true;
        state.error = null;
      })
      .addCase(fetchActioncardsLocationsUnits.fulfilled, (state, action) => {
        state.optionsLoading = false;
        state.locationOptions = action.payload || [];
      })
      .addCase(fetchActioncardsLocationsUnits.rejected, (state, action) => {
        state.optionsLoading = false;
        if (action.meta.aborted) return;
        state.error = action.payload;
      })

      .addCase(updateActioncardsSetBusinessUnits.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(updateActioncardsSetBusinessUnits.fulfilled, (state) => {
        state.saving = false;
        state.successMessage = "Actioncards set updated successfully";
      })
      .addCase(updateActioncardsSetBusinessUnits.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearActioncardsSetToast, resetActioncardsSetState } =
  actioncardsSetSlice.actions;

export default actioncardsSetSlice.reducer;
